import { Plus, Repeat } from "lucide-react";
import { useGetList, useTranslate } from "ra-core";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { roundMoney } from "../../providers/commons/financeSchedule";
import type { FinanceRecurringExpense } from "../../types";
import { useFormatMoney } from "../useFormatMoney";
import { RecurringExpenseDialog } from "./RecurringExpenseDialog";
import { useCategoryLabel } from "./useCategoryLabel";

// A team keeps a few dozen templates at most
const MAX_TEMPLATES = 200;

/** Recurring expense templates and the fixed monthly cost they add up to */
export const RecurringExpenseList = () => {
  const translate = useTranslate();
  const formatMoney = useFormatMoney();
  const categoryLabel = useCategoryLabel();
  const { data: templates = [], isPending } =
    useGetList<FinanceRecurringExpense>("finance_recurring_expenses", {
      pagination: { page: 1, perPage: MAX_TEMPLATES },
      sort: { field: "description", order: "ASC" },
    });
  const [editing, setEditing] = useState<FinanceRecurringExpense | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const monthlyTotal = roundMoney(
    templates
      .filter((template) => template.active)
      .reduce((sum, template) => sum + template.amount, 0),
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {translate("crm.finance.recurring.total", {
            amount: formatMoney(monthlyTotal),
          })}
        </p>
        <Button type="button" onClick={() => setIsCreating(true)}>
          <Plus />
          {translate("crm.finance.recurring.create")}
        </Button>
      </div>
      {isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : templates.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-12 text-center text-sm text-muted-foreground">
          <Repeat className="size-8" />
          {translate("crm.finance.recurring.empty")}
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
          {templates.map((template) => (
            <li key={template.id}>
              <button
                type="button"
                onClick={() => setEditing(template)}
                className="flex w-full flex-col gap-1 rounded-lg border p-3 text-left hover:bg-accent"
                data-testid="recurring-row"
              >
                <span className="flex items-start justify-between gap-2">
                  <span className="truncate font-medium">
                    {template.description}
                  </span>
                  <span className="font-semibold whitespace-nowrap">
                    {formatMoney(template.amount)}
                  </span>
                </span>
                <span className="flex items-center gap-2 text-xs text-muted-foreground">
                  {categoryLabel(template.category)}
                  {" · "}
                  {translate("crm.finance.recurring.due_day", {
                    day: template.due_day,
                  })}
                  {!template.active && (
                    <Badge variant="outline">
                      {translate("crm.finance.recurring.inactive")}
                    </Badge>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <RecurringExpenseDialog
        open={isCreating || editing != null}
        template={editing}
        onClose={() => {
          setIsCreating(false);
          setEditing(null);
        }}
      />
    </div>
  );
};
